import React from "react";
import { Link } from "react-router-dom";

/**
 * Legal pages — Privacy, Terms, Subscription Terms, Refund, Cookies.
 *
 * These templates are drafted from the standard SaaS + auto-renewing subscription
 * boilerplate that PJP's counsel will refine before public launch. Do NOT treat as
 * final legal advice — Jeff should have Acrisure legal review before production go-live.
 */
const EFFECTIVE = "February 21, 2026";
const COMPANY = "Private Jet Pilots, LLC (\"PJP\", \"we\", \"us\")";
const CONTACT_EMAIL = "privacy@privatejetpilots.com";

function Shell({ title, subtitle, children, testId }) {
  return (
    <div className="max-w-4xl mx-auto px-6 py-14" data-testid={testId}>
      <div className="mono text-[11px] text-cyan tracking-widest mb-2">// LEGAL</div>
      <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight mb-3">{title}</h1>
      {subtitle && <p className="text-white/60 mb-6">{subtitle}</p>}
      <div className="mono text-[10px] text-white/45 mb-10">Effective {EFFECTIVE}</div>
      <div className="prose prose-invert prose-sm max-w-none space-y-6 text-white/75 leading-relaxed">
        {children}
      </div>
      <div className="mt-16 pt-6 border-t border-white/[0.06] text-[11px] text-white/40 mono">
        Questions? <a href={`mailto:${CONTACT_EMAIL}`} className="text-cyan hover:underline">{CONTACT_EMAIL}</a>
        {" · "}
        <Link to="/contact" className="text-cyan hover:underline">Contact form</Link>
      </div>
    </div>
  );
}

function Section({ n, title, children }) {
  return (
    <section>
      <h2 className="font-display text-xl font-semibold text-white mt-8 mb-3">
        <span className="text-cyan mono text-sm mr-3">{String(n).padStart(2, "0")}</span>
        {title}
      </h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
export function Privacy() {
  return (
    <Shell testId="page-privacy" title="Privacy Policy" subtitle="How we collect, use, and protect your data.">
      <p>
        {COMPANY} respects your privacy. This Privacy Policy describes the information we
        collect from members of PrivateJetPilots.com and our mobile and web applications
        (collectively, the "Services"), how we use it, and the choices you have.
      </p>

      <Section n={1} title="Information We Collect">
        <p><strong>Account data:</strong> name, email, phone, password hash, aviation role (pilot, mechanic,
        operator, etc.), certificate numbers, home base ICAO, ratings, employment history you
        voluntarily submit through the Pilot History Form ("PHF").</p>
        <p><strong>FAA cross-check data:</strong> when you provide a first + last name we perform a
        best-effort lookup against the public FAA Airmen Registry to verify your credentials.
        We store only the returned certificate class, ratings, and issuance date.</p>
        <p><strong>Billing data:</strong> handled by Stripe. We store a customer ID and subscription
        metadata (tier, start date, renewal date, status) — we never see or store your card
        number, CVC, or bank details.</p>
        <p><strong>Usage data:</strong> log-ins, feature usage, IP address, device type, and interaction
        events used to improve the Service and detect abuse.</p>
      </Section>

      <Section n={2} title="How We Use Your Data">
        <ul className="list-disc pl-6 space-y-1">
          <li>Operate the platform (match trips, deliver AOG alerts, generate PDFs).</li>
          <li>Verify your identity and FAA credentials.</li>
          <li>Process membership payments and communicate renewals.</li>
          <li>Send safety notices, product updates, and — with consent — marketing emails.</li>
          <li>Detect fraud, illegal charter, and violations of our Terms.</li>
        </ul>
      </Section>

      <Section n={3} title="Sharing">
        <p>We do <strong>not</strong> sell personal information. We share data only with:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>Service providers</strong> (Stripe, Resend, Twilio, cloud hosting) under contract.</li>
          <li><strong>Verified operators / employers</strong> you explicitly apply to.</li>
          <li><strong>Law enforcement</strong> when required by valid legal process.</li>
        </ul>
      </Section>

      <Section n={4} title="Your Rights">
        <p>You may access, export, correct, or delete your data at any time from{" "}
        <Link to="/settings/account" className="text-cyan hover:underline">Account Settings</Link>{" "}
        or by emailing <a href={`mailto:${CONTACT_EMAIL}`} className="text-cyan hover:underline">{CONTACT_EMAIL}</a>.
        California (CCPA/CPRA), Virginia (VCDPA), Colorado (CPA), and EU (GDPR) residents have
        additional rights we honor globally.</p>
      </Section>

      <Section n={5} title="Retention & Security">
        <p>We retain account data while your membership is active and for 30 days after
        deletion for audit/legal holds. Backups are purged within 90 days. Data at rest is
        encrypted (AES-256) and in transit (TLS 1.2+).</p>
      </Section>

      <Section n={6} title="Cookies">
        <p>See our <Link to="/cookie-policy" className="text-cyan hover:underline">Cookie Policy</Link>{" "}for
        details on the cookies we set and how to opt out.</p>
      </Section>

      <Section n={7} title="Changes">
        <p>We will notify members of material changes by email and by posting an update to this
        page at least 30 days before they take effect.</p>
      </Section>
    </Shell>
  );
}

/* ------------------------------------------------------------------ */
export function Terms() {
  return (
    <Shell testId="page-terms" title="Terms of Service" subtitle="The agreement between you and PJP.">
      <Section n={1} title="Acceptance">
        <p>By creating an account or using the Services you agree to these Terms and our{" "}
          <Link to="/privacy" className="text-cyan hover:underline">Privacy Policy</Link>. If you do not
          agree, do not use the Services.</p>
      </Section>
      <Section n={2} title="Eligibility">
        <p>You must be at least 18 and legally able to enter into a binding contract. Business
          accounts (Charter Operators, FBOs) must be created by an authorized representative.</p>
      </Section>
      <Section n={3} title="Membership & Payment">
        <p>Some features require a paid membership. Fees, renewal cadence, and cancellation
          rules are described in our{" "}
          <Link to="/subscription-terms" className="text-cyan hover:underline">Subscription Terms</Link>.</p>
      </Section>
      <Section n={4} title="Acceptable Use">
        <ul className="list-disc pl-6 space-y-1">
          <li>No illegal 91-versus-135 charter arrangements; PJP monitors for compliance.</li>
          <li>No misrepresentation of certificates, medicals, or currency.</li>
          <li>No harvesting of member data, scraping, or reselling of directory content.</li>
          <li>No harassing, defamatory, or discriminatory content in forums or messaging.</li>
        </ul>
      </Section>
      <Section n={5} title="User Content">
        <p>You retain ownership of content you post. You grant PJP a worldwide, royalty-free
          license to host, display, and distribute that content within the Services. You are
          solely responsible for the accuracy of content you submit (including PHF entries).</p>
      </Section>
      <Section n={6} title="Third-Party Services">
        <p>The Services integrate with Stripe, adsb.lol, Acrisure, Resend, and others. Their
          terms and privacy policies apply to their respective portions of the Service.</p>
      </Section>
      <Section n={7} title="Disclaimers">
        <p>The Services are provided "AS IS". PJP does not guarantee employment, insurability,
          or the accuracy of FAA data returned by third-party registries.</p>
      </Section>
      <Section n={8} title="Limitation of Liability">
        <p>To the maximum extent permitted by law, PJP's total liability is limited to the
          greater of (a) fees you paid in the 12 months preceding the claim or (b) US $100.</p>
      </Section>
      <Section n={9} title="Governing Law & Dispute Resolution">
        <p>These Terms are governed by the laws of the State of Florida. Disputes will be
          resolved by binding arbitration in Miami-Dade County under the AAA Consumer Rules,
          except that either party may bring an individual small-claims action. You waive the
          right to a jury trial and to participate in a class action.</p>
      </Section>
      <Section n={10} title="Contact">
        <p><a href={`mailto:${CONTACT_EMAIL}`} className="text-cyan hover:underline">{CONTACT_EMAIL}</a></p>
      </Section>
    </Shell>
  );
}

/* ------------------------------------------------------------------ */
export function SubscriptionTerms() {
  return (
    <Shell
      testId="page-subscription"
      title="Subscription & Auto-Renewal Terms"
      subtitle="Everything about billing, renewals, and cancellations."
    >
      <div className="rounded-lg border border-orange-hud/40 bg-orange-hud/[0.05] px-4 py-3 mono text-[11px] text-orange-hud">
        AUTO-RENEWAL DISCLOSURE. Your PJP membership renews automatically each year on the
        anniversary of your enrollment at the then-current published rate unless you cancel
        before the renewal date.
      </div>

      <Section n={1} title="Membership Tiers">
        <p>Current tiers and pricing are listed on the{" "}
          <Link to="/membership" className="text-cyan hover:underline">Membership page</Link>.
          Prices are quoted in USD and exclude applicable taxes.</p>
      </Section>

      <Section n={2} title="Billing Cycle">
        <p>Memberships are billed <strong>annually in advance</strong>. Your billing cycle begins on the
          date you first successfully pay ("Enrollment Date") and renews on the same calendar
          date each year ("Renewal Date").</p>
      </Section>

      <Section n={3} title="Automatic Renewal">
        <p>By enrolling you authorize PJP (via Stripe) to charge your saved payment method
          the applicable annual fee on each Renewal Date. If the payment fails we will retry
          for up to 14 days before suspending access.</p>
      </Section>

      <Section n={4} title="Renewal Reminder">
        <p>We will email a renewal reminder to your account email at least <strong>30 days before
          each Renewal Date</strong>. The reminder will include the amount, the payment method on
          file, and instructions to cancel.</p>
      </Section>

      <Section n={5} title="Cancellation">
        <p>You may cancel at any time from{" "}
          <Link to="/settings/account" className="text-cyan hover:underline">Account Settings → Subscription → Cancel</Link>.
          Cancellation takes effect at the end of the then-current paid period. You retain
          full access to paid features until that date. No prorated refunds are issued for
          partial billing periods (see <Link to="/refund-policy" className="text-cyan hover:underline">Refund Policy</Link>).</p>
      </Section>

      <Section n={6} title="Price Changes">
        <p>We may change subscription pricing. Any increase will apply only to renewals
          occurring at least 30 days after we notify you.</p>
      </Section>

      <Section n={7} title="Account Deletion vs. Cancellation">
        <p><strong>Cancellation</strong> stops future charges but keeps your data. <strong>Account deletion</strong>
          (also available in Account Settings) immediately terminates your subscription and
          permanently purges your data — this is irreversible.</p>
      </Section>
    </Shell>
  );
}

/* ------------------------------------------------------------------ */
export function RefundPolicy() {
  return (
    <Shell testId="page-refund" title="Refund Policy" subtitle="When we issue refunds — and when we don't.">
      <Section n={1} title="Annual Memberships">
        <p>Annual memberships are <strong>non-refundable</strong> once the billing period has begun,
          except as required by applicable law. Cancellation stops future renewals but does
          not prorate the current period.</p>
      </Section>
      <Section n={2} title="Add-On Purchases">
        <p>One-time purchases (Digital ID NFC card, branded PDF exports, Hire Crew Now
          job posts) are <strong>non-refundable</strong> once delivered.</p>
      </Section>
      <Section n={3} title="Duplicate Charges & Errors">
        <p>If you are charged in error or double-charged, contact{" "}
          <a href={`mailto:${CONTACT_EMAIL}`} className="text-cyan hover:underline">{CONTACT_EMAIL}</a>{" "}
          within 60 days for a full refund of the erroneous amount.</p>
      </Section>
      <Section n={4} title="Statutory Rights">
        <p>Nothing in this policy limits your statutory rights under consumer-protection laws
          in your jurisdiction.</p>
      </Section>
    </Shell>
  );
}

/* ------------------------------------------------------------------ */
export function CookiePolicy() {
  return (
    <Shell testId="page-cookies" title="Cookie Policy" subtitle="Cookies we set and how to control them.">
      <Section n={1} title="What are cookies?">
        <p>Cookies are small text files stored on your device that let websites remember your
          preferences and login state.</p>
      </Section>
      <Section n={2} title="Categories we use">
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>Strictly necessary:</strong> session token, CSRF, load-balancing.</li>
          <li><strong>Preferences:</strong> theme, timezone, dashboard layout.</li>
          <li><strong>Analytics:</strong> anonymized usage patterns to improve the Service.</li>
        </ul>
        <p>We do not run advertising cookies.</p>
      </Section>
      <Section n={3} title="Controls">
        <p>You can clear or block cookies via your browser settings. Blocking strictly-necessary
          cookies will prevent you from logging in.</p>
      </Section>
    </Shell>
  );
}
